import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge, Box, Code, Flex, Text } from "@radix-ui/themes";
import { AlertTriangle, Info } from "lucide-react";
import SearchableSelect from "@/components/ui/SearchableSelect";
import HelpPopover from "@/components/ui/HelpPopover";
import {
  PushCategory,
  tenantPushService,
} from "@/services/breeze/TenantPushService";
import { PushButtonRow } from "./PushButtonsField";

interface Props {
  value: string;
  onChange: (next: string) => void;
  buttons: PushButtonRow[];
  onAutofillButtons?: (rows: PushButtonRow[]) => void;
}

export default function IosButtonGroupPicker({
  value,
  onChange,
  buttons,
  onAutofillButtons,
}: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ["push-categories"],
    queryFn: () => tenantPushService.getCategories(),
  });

  const synced = useMemo<PushCategory[]>(
    () => (data?.categories ?? []).filter((c) => (c.ios_action_order?.length ?? 0) > 0),
    [data],
  );
  const options = useMemo(
    () => synced.map((c) => ({
      value: c.category,
      label: c.label ? `${c.label} (${c.category})` : c.category,
    })),
    [synced],
  );
  const selected = useMemo(
    () => synced.find((c) => c.category === value),
    [synced, value],
  );

  // Re-run when `selected` resolves after first paint (saved category, late categories query).
  const registeredKey = (selected?.ios_action_order ?? []).join(",");
  useEffect(() => {
    if (!selected || !onAutofillButtons) return;
    const registered = selected.ios_action_order ?? [];
    if (registered.length === 0) return;
    const labels = selected.ios_labels ?? {};
    const existingByid = new Map(buttons.map((b) => [b.id, b]));
    const isAlreadyAligned =
      buttons.length === registered.length
      && buttons.every((b, i) => b.id === registered[i]);
    if (isAlreadyAligned) return;
    onAutofillButtons(
      registered.map((id) => {
        const existing = existingByid.get(id);
        return {
          id,
          label: labels[id] ?? existing?.label ?? id,
          click_action: existing?.click_action ?? "",
        };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, registeredKey]);

  const sentIds = new Set(buttons.map((b) => b.id));
  const registeredIds = new Set(selected?.ios_action_order ?? []);
  const unknownInSent = buttons
    .map((b) => b.id)
    .filter((id) => id && selected && !registeredIds.has(id));

  return (
    <Box>
      <Flex align="center" gap="1" mb="1">
        <Text as="label" size="2" weight="medium">
          iOS button group
        </Text>
        <HelpPopover title="Why does iOS need this?">
          Unlike Android and Web, iOS won't show notification action buttons unless the app has pre-registered them at launch.
          A "button group" is one of those pre-registered sets. Pick a group here and iOS will render the buttons it knows about under that id.
          Android and Web ignore this and use the labels you type below.
        </HelpPopover>
      </Flex>
      {synced.length === 0 ? (
        <Box
          p="2"
          style={{
            border: "1px solid var(--gray-5)",
            borderRadius: "var(--radius-2)",
            background: "var(--gray-2)",
          }}
        >
          <Flex align="start" gap="2">
            <Info size={14} />
            <Text size="1" color="gray">
              No iOS button groups have been registered yet. The app reports its registered groups via{" "}
              <Code size="1" variant="ghost">POST /push/categories/sync</Code> on boot. Until that runs,
              iOS won't render action buttons — Android and Web still will.
            </Text>
          </Flex>
        </Box>
      ) : (
        <>
          <SearchableSelect
            value={value}
            onChange={onChange}
            options={[
              { value: "", label: isLoading ? "Loading…" : "— None (iOS won't show buttons) —" },
              ...options,
            ]}
            placeholder="Pick a registered group"
            searchPlaceholder="Search groups…"
          />
          {selected && (
            <Box mt="2" p="2" style={{
              border: "1px solid var(--gray-5)",
              borderRadius: "var(--radius-2)",
              background: "var(--gray-2)",
            }}>
              <Text size="1" color="gray" mb="1" style={{ display: "block" }}>
                <strong>Registered for iOS</strong>{selected.registered_at_version && (
                  <> (app v{selected.registered_at_version})</>
                )} — buttons appear in this order with these labels:
              </Text>
              <Flex direction="column" gap="1">
                {(selected.ios_action_order ?? []).map((id, i) => (
                  <Flex key={id} gap="2" align="center">
                    <Text size="1" color="gray">{i + 1}.</Text>
                    <Code size="1" variant="ghost">{id}</Code>
                    <Text size="1">→</Text>
                    <Text size="1">
                      {selected.ios_labels?.[id] ?? id}
                    </Text>
                    {!sentIds.has(id) && (
                      <Badge size="1" color="gray" variant="soft">missing from buttons</Badge>
                    )}
                  </Flex>
                ))}
              </Flex>
            </Box>
          )}
          {unknownInSent.length > 0 && (
            <Box mt="2" p="2" style={{
              border: "1px solid var(--red-6)",
              borderRadius: "var(--radius-2)",
              background: "var(--red-2)",
            }}>
              <Flex align="start" gap="2">
                <AlertTriangle size={14} color="var(--red-11)" />
                <Text size="1" color="red">
                  Action id{unknownInSent.length > 1 ? "s" : ""}{" "}
                  {unknownInSent.map((id, i) => (
                    <span key={id}>
                      {i > 0 && ", "}
                      <Code size="1" variant="ghost">{id}</Code>
                    </span>
                  ))}{" "}
                  {unknownInSent.length > 1 ? "are" : "is"} not registered under this group.
                  The send will fail with <Code size="1" variant="ghost">422</Code>.
                </Text>
              </Flex>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
